import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Box, Typography, Stack, Button, Chip } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useTheme } from "@mui/material/styles";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import UserContext from "./UserContext";

const B = {
  navy:     "#00357a",
  navyDark: "#001f4d",
  navyMid:  "#004299",
  gold:     "#c9a96e",
  goldLight:"#dfc08a",
  offWhite: "#f8f6f2",
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const DOT_GRID_LIGHT =
  "radial-gradient(circle, rgba(0,53,122,0.09) 1.2px, transparent 1.2px)";
const DOT_GRID_DARK =
  "radial-gradient(circle, rgba(255,255,255,0.045) 1.2px, transparent 1.2px)";

const TRUST = ["HIPAA Compliant", "256-bit SSL", "SOC 2 Type II", "Zero PHI to AI"];

const SHARED_FEATURES = [
  { icon: "🧪", text: "AI-powered lab report analysis" },
  { icon: "🔒", text: "HIPAA-compliant & private" },
  { icon: "🌐", text: "16-language support" },
  { icon: "📊", text: "Longitudinal trend memory" },
];

const PATIENT_CARD = {
  id: "patient",
  path: "/patient-bot",
  badge: "PATIENT",
  title: "AIden — Patient Assistant",
  tagline: "Understand your results in plain language",
  description:
    "Upload your Labarium lab report to AIden to receive “Aid” from Labarium’s AI assistant in clearly explaining your test results – what it means, what to consider next, what you can do in your daily life to improve your health and wellness, and what questions to ask your clinicians.",
  note: "Note: if you upload a non-Labarium test result, the Decision Assistant tool will not function properly.",
  disclaimer:
    "DISCLAIMER: Outputs generated by this software are intended solely for non-medical, general wellness purposes. Like all AI, AIden may occasionally misread values or make errors, and output quality depends on the clarity and completeness of the uploaded report. This product is not intended to diagnose, treat, cure, or prevent any disease, and cannot substitute for a professional medical evaluation, consultation, or treatment. Always verify results with a licensed healthcare provider.",
  accentFrom: "#3b82f6",
  accentTo:   "#6366f1",
  features: SHARED_FEATURES,
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="16" r="8" stroke="currentColor" strokeWidth="2.5" />
      <path d="M8 40c0-8.837 7.163-16 16-16s16 7.163 16 16"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

const CLINICIAN_CARD = {
  id: "clinician",
  path: "/clinician-bot",
  badge: "CLINICIAN",
  title: "ClAIre — Decision Support Assistant",
  tagline: "Evidence-based decision support for licensed providers",
  description:
    "Evidence-based data analysis for licensed clinicians from ClAIre provides a “clear” picture turning a data point into a contextual analysis – risk stratification, recommendations to consider, protocols, and peer-reviewed citations.",
  note: "Note: if you upload a non-Labarium test result, the Decision Assistant tool will not function properly.",
  disclaimer:
    "DISCLAIMER: This Clinical Decision Support tool is intended solely to provide recommendations for healthcare professionals. Like all AI, ClAIre may occasionally misread values or make errors, particularly if the uploaded report is unclear, incomplete, or in a non-standard format. It is not a substitute for independent clinical judgment, diagnosis, or treatment. Healthcare professionals must independently review and verify all information and recommendations generated by this software.",
  accentFrom: "#10b981",
  accentTo:   "#0891b2",
  features: SHARED_FEATURES,
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="12" y="6" width="24" height="36" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <path d="M18 16h12M18 22h12M18 28h8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="34" cy="36" r="6" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2" />
      <path d="M32 36h4M34 34v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

const REPORTS_CARD = {
  id: "reports",
  path: "/my-reports",
  badge: "REPORTS",
  title: "Labarium AI Reports",
  tagline: "Your personal report archive, always at hand",
  description: "Browse, preview, and download every lab report that has been processed through Labarium — organised chronologically in your private, encrypted folder. Only you can see your reports.",
  note: null,
  disclaimer: null,
  accentFrom: "#f59e0b",
  accentTo: "#ef4444",
  features: [
    { icon: "📂", text: "Folder-based chronological archive" },
    { icon: "👁️", text: "Inline PDF preview" },
    { icon: "⬇️", text: "One-click secure download" },
    { icon: "🔒", text: "Private — only your reports" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="6" y="10" width="36" height="30" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 18h36" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 10l8-6h28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M18 28h12M24 24v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

// P7 — new. Same gating convention as REPORTS_CARD (env-var toggle) so
// this can be rolled out independently of the GCS-browsing feature above.
const SUMMARIES_CARD = {
  id: "summaries",
  path: "/my-summaries",
  badge: "SUMMARIES",
  title: "My Summaries",
  tagline: "A plain-language summary of every test, as soon as it's ready",
  description: "See an AI-generated, easy-to-read summary of each completed test — no need to open a PDF or ask a question first.",
  note: null,
  disclaimer: null,
  accentFrom: "#0891b2",
  accentTo: "#3b82f6",
  features: [
    { icon: "📝", text: "Plain-language summary per test" },
    { icon: "🕑", text: "Newest first, always up to date" },
    { icon: "🔒", text: "Private — only your results" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <path d="M10 10h20l8 8v20a2 2 0 01-2 2H10a2 2 0 01-2-2V12a2 2 0 012-2z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M16 22h16M16 28h16M16 34h10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// My Results — "My Results" ecosystem plan, Phase 1 (core page). Patient-
// only, same gating convention as MY_DIGEST_CARD below (env-var toggle,
// must match the backend's MY_RESULTS_ENABLED — not auto-synced, set both
// when toggling).
const MY_RESULTS_CARD = {
  id: "results",
  path: "/my-results",
  badge: "RESULTS",
  title: "My Results",
  tagline: "Every test value, where it falls, and what it means",
  description: "See each of your results plotted against its own reference range, with a plain-language explanation for anything out of range.",
  note: null,
  disclaimer: null,
  accentFrom: "#00357a",
  accentTo: "#c9a96e",
  features: [
    { icon: "📊", text: "Every value, visually ranged" },
    { icon: "💬", text: "Plain-language explanations" },
    { icon: "🔒", text: "Private — only your results" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <path d="M8 38V18M20 38V10M32 38V24M44 38V16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M4 38h40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// "My Results" ecosystem plan, Phase 2 — clinician counterpart of
// MY_RESULTS_CARD above. Clinician-only, same gating pattern as
// MY_PATIENTS_CARD/ORDER_TEST_CARD.
const MY_PATIENTS_RESULTS_CARD = {
  id: "patients-results",
  path: "/my-patients-results",
  badge: "RESULTS",
  title: "My Patients' Results",
  tagline: "Completed test results for your own patients",
  description: "See clinical flags, citations, and full structured values for every completed report tied to your own orders.",
  note: null,
  disclaimer: null,
  accentFrom: "#0891b2",
  accentTo: "#c9a96e",
  features: [
    { icon: "🚩", text: "Clinical flags & citations" },
    { icon: "📊", text: "Full structured values" },
    { icon: "🔒", text: "Private — only your own patients" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <path d="M8 38V18M20 38V10M32 38V24M44 38V16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M4 38h40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// 2026-10-22 addition — clinician counterpart of SUMMARIES_CARD, split
// out from MY_PATIENTS_RESULTS_CARD above per explicit user instruction
// (that page used to also show ai_summary; now cleanly separated,
// mirroring the patient side's own My Results/My Summaries split).
// Gated by its own "summaries" flag (mySummariesEnabled), not
// myResultsEnabled.
const MY_PATIENTS_SUMMARIES_CARD = {
  id: "patients-summaries",
  path: "/my-patients-summaries",
  badge: "SUMMARIES",
  title: "My Patients' Summaries",
  tagline: "A plain-language summary of every patient's completed test",
  description: "See an AI-generated, easy-to-read summary for each of your patients' completed tests — no need to open a PDF first.",
  note: null,
  disclaimer: null,
  accentFrom: "#0891b2",
  accentTo: "#3b82f6",
  features: [
    { icon: "📝", text: "Plain-language summary per test" },
    { icon: "🕑", text: "Newest first, always up to date" },
    { icon: "🔒", text: "Private — only your own patients" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <path d="M10 10h20l8 8v20a2 2 0 01-2 2H10a2 2 0 01-2-2V12a2 2 0 012-2z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M16 22h16M16 28h16M16 34h10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// New — patient-facing payment status + receipt (staff already had this
// via labariumaireport's RCM dashboard; this is the first patient-side
// view). Patients only — clinicians have no payment_status field at all
// (they're never charged this copay), so this card is gated accordingly
// below (role-based, not an env flag — SUMMARIES_CARD is gated by its own
// mySummariesEnabled flag instead, see its own card definition above).
const PAYMENTS_CARD = {
  id: "payments",
  path: "/my-payment",
  badge: "PAYMENTS",
  title: "My Payments",
  tagline: "Your copay, panel payments, and receipts, in one place",
  description: "Check your Lab Test Copay and Activation Fee status, pay for any lab test panel directly, and download your receipts anytime.",
  note: null,
  disclaimer: null,
  accentFrom: "#c9a96e",
  accentTo: "#00357a",
  features: [
    { icon: "💳", text: "Payment status at a glance" },
    { icon: "🧾", text: "Receipt, one click away" },
    { icon: "🔒", text: "Private — only your record" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="8" y="14" width="32" height="22" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <path d="M8 20h32" stroke="currentColor" strokeWidth="2.5" />
      <path d="M14 28h10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// My Digest — patient-only, same gating convention as PAYMENTS_CARD. Added
// 2026-10-16 (Open CEO Decision #13 close-out) — the digest-ready email was
// otherwise the ONLY way a patient could ever find this page.
const MY_DIGEST_CARD = {
  id: "digest",
  path: "/my-digest",
  badge: "DIGEST",
  title: "My Digest",
  tagline: "Your monthly AI health insights check-in",
  description: "A plain-language monthly check-in on your results, plus relevant health news — available with an active subscription.",
  note: null,
  disclaimer: null,
  accentFrom: "#7c3aed",
  accentTo: "#0891b2",
  features: [
    { icon: "📰", text: "Plain-language monthly check-in" },
    { icon: "🗞️", text: "Relevant health news" },
    { icon: "🔒", text: "Private — only your results" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="9" y="8" width="30" height="32" rx="2" stroke="currentColor" strokeWidth="2.5" />
      <path d="M15 17h18M15 24h18M15 31h10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// Order a Test — clinician-only, same gating convention as PAYMENTS_CARD.
const ORDER_TEST_CARD = {
  id: "order-test",
  path: "/order-test",
  badge: "ORDER",
  title: "Order a Test",
  tagline: "Order a Labarium test for your patient, start to finish",
  description: "Select a test, select or add a patient, complete the requisition, and sign — delivered directly to the lab.",
  note: null,
  disclaimer: null,
  accentFrom: "#00357a",
  accentTo: "#c9a96e",
  features: [
    { icon: "🧪", text: "Search Labarium's test catalog" },
    { icon: "👤", text: "Returning patients autofill" },
    { icon: "✍️", text: "Sign and submit in one visit" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <path d="M18 6h12v10l9 20a3 3 0 01-2.8 4.2H11.8A3 3 0 019 36l9-20V6z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M14 30h20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M18 6h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

// My Patients — clinician-only. Order history + editable demographics,
// scoped strictly to this clinician's own account.
const MY_PATIENTS_CARD = {
  id: "my-patients",
  path: "/my-patients",
  badge: "PATIENTS",
  title: "My Patients",
  tagline: "Your patients and their order history, in one place",
  description: "Review and update patient demographics, and see every test you've ordered for them — each with its signed requisition.",
  note: null,
  disclaimer: null,
  accentFrom: "#0891b2",
  accentTo: "#00357a",
  features: [
    { icon: "🔍", text: "Search by name or date of birth" },
    { icon: "📋", text: "Full order history per patient" },
    { icon: "🔒", text: "Private — only your own patients" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <circle cx="18" cy="16" r="7" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 40c0-8 5.5-13 12-13s12 5 12 13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M32 14h10M37 9v10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

const STATS = [
  { value: "16+",   label: "Languages" },
  { value: "99%",   label: "Accuracy" },
  { value: "HIPAA", label: "Compliant" },
  { value: "24/7",  label: "Available" },
];

function StatPill({ value, label, isDark }) {
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center",
      px: { xs: 2, sm: 3 }, py: 1.5,
      borderRadius: "12px",
      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.04)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,122,0.08)"}`,
      minWidth: 72,
    }}>
      <Typography sx={{
        fontSize: { xs: 18, sm: 22 }, fontWeight: 800, lineHeight: 1,
        background: `linear-gradient(135deg, ${B.gold} 20%, ${B.goldLight} 80%)`,
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
      }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.1em",
        color: isDark ? "#64748b" : "#94a3b8", mt: 0.4, textTransform: "uppercase" }}>
        {label}
      </Typography>
    </Box>
  );
}

function highlightAI(text) {
  const idx = text.indexOf("AI");
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ color: B.gold, fontWeight: 900 }}>AI</Box>
      {text.slice(idx + 2)}
    </>
  );
}

const AVATAR = {
  patient:   "/aiden-avatar.png",
  clinician: "/claire-avatar.png",
};

function RoleCard({ card, isDark, onClick }) {
  const [hovered, setHovered] = useState(false);

  const bg      = isDark ? "rgba(17,24,39,0.9)" : "rgba(255,255,255,0.92)";
  const textSub = isDark ? "#94a3b8" : "#475569";
  const avatarSrc = AVATAR[card.id] || null;

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={onClick}
      whileHover={{ y: -8, scale: 1.01 }}
      whileTap={{ scale: 0.985 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
    >
      <Box sx={{
        height: "100%",
        display: "flex", flexDirection: "column",
        borderRadius: "20px",
        overflow: "hidden",
        position: "relative",
        background: isDark
          ? "linear-gradient(rgba(17,24,39,0.9), rgba(17,24,39,0.9)) padding-box, linear-gradient(135deg, rgba(0,53,122,0.6), rgba(192,168,140,0.4)) border-box"
          : "linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)) padding-box, linear-gradient(135deg, rgba(0,53,122,0.25), rgba(192,168,140,0.35)) border-box",
        border: "1.5px solid transparent",
        backdropFilter: "blur(12px)",
        boxShadow: hovered
          ? isDark
            ? `0 28px 72px rgba(0,0,0,0.5), 0 0 0 1px rgba(192,168,140,0.25), inset 0 1px 0 rgba(255,255,255,0.06)`
            : `0 28px 72px rgba(0,53,122,0.18), 0 0 0 1px rgba(192,168,140,0.3), inset 0 1px 0 rgba(255,255,255,0.8)`
          : isDark
            ? "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)"
            : "0 4px 24px rgba(0,53,122,0.08), inset 0 1px 0 rgba(255,255,255,1)",
        transition: "box-shadow 0.3s ease",
      }}>

        {/* Gradient accent bar */}
        <Box sx={{
          height: 3,
          background: `linear-gradient(to right, ${card.accentFrom}, ${card.accentTo}, ${B.gold})`,
          opacity: hovered ? 1 : 0.6,
          transition: "opacity 0.3s",
          flexShrink: 0,
        }} />

        {/* Header */}
        <Box sx={{
          px: 3.5, pt: 3, pb: 3, position: "relative", overflow: "hidden", flexShrink: 0,
          background: `linear-gradient(135deg, ${B.navy} 0%, ${B.navyMid} 100%)`,
        }}>
          <Box sx={{ position: "absolute", top: -60, right: -60, width: 180, height: 180,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.1)" }} />
          <Box sx={{ position: "absolute", top: -36, right: -36, width: 110, height: 110,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.16)" }} />

          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.75,
            bgcolor: "rgba(192,168,140,0.15)",
            border: "1px solid rgba(192,168,140,0.25)",
            borderRadius: "99px", px: 1.5, py: 0.4, mb: 2,
          }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: B.gold,
              "@keyframes pulse2": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
              animation: "pulse2 2s infinite" }} />
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: B.gold, letterSpacing: "0.15em" }}>
              {card.badge}
            </Typography>
          </Box>

          {avatarSrc ? (
            <motion.div
              animate={{ scale: hovered ? 1.04 : 1 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              style={{ marginBottom: 16, display: "inline-block" }}
            >
              <Box sx={{
                width: 96, height: 96, borderRadius: "50%", overflow: "hidden",
                border: `2.5px solid rgba(192,168,140,0.55)`,
                boxShadow: hovered
                  ? "0 0 0 4px rgba(192,168,140,0.18), 0 8px 32px rgba(0,0,0,0.45)"
                  : "0 0 0 2px rgba(192,168,140,0.12), 0 4px 20px rgba(0,0,0,0.35)",
                transition: "box-shadow 0.3s ease",
              }}>
                <img
                  src={avatarSrc}
                  alt={card.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 12%" }}
                />
              </Box>
            </motion.div>
          ) : (
            <motion.div animate={{ rotate: hovered ? 10 : 0 }} transition={{ duration: 0.3 }}>
              <Box sx={{ color: B.gold, mb: 1.25 }}>{card.icon}</Box>
            </motion.div>
          )}

          <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1.25, mb: 0.5 }}>
            {highlightAI(card.title)}
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: "rgba(255,255,255,0.72)", lineHeight: 1.5 }}>
            {card.tagline}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3.5, py: 3, flex: 1, display: "flex", flexDirection: "column", bgcolor: bg }}>
          <Typography sx={{ fontSize: 15, color: textSub, lineHeight: 1.85, mb: 3 }}>
            {highlightAI(card.description)}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
            {card.features.map((f, i) => (
              <motion.div
                key={i}
                animate={{ x: hovered ? 5 : 0, opacity: hovered ? 1 : 0.9 }}
                transition={{ delay: hovered ? i * 0.04 : 0, duration: 0.2 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: "8px", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,53,122,0.05)",
                    fontSize: 14,
                  }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: 14.5, color: isDark ? "#cbd5e1" : "#334155", fontWeight: 500 }}>
                    {f.text}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          {/* CTA */}
          <Box sx={{ mt: 3.5 }}>
            <Button
              fullWidth
              variant="contained"
              endIcon={
                <motion.div animate={{ x: hovered ? 4 : 0 }} transition={{ duration: 0.2 }}>
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </motion.div>
              }
              sx={{
                bgcolor: B.navy, color: "#fff",
                fontWeight: 700, fontSize: 15, py: 1.4,
                borderRadius: "10px", letterSpacing: "0.02em", textTransform: "none",
                boxShadow: hovered ? "0 8px 24px rgba(0,53,122,0.4)" : "0 2px 8px rgba(0,53,122,0.2)",
                transition: "all 0.25s ease",
                background: hovered ? `linear-gradient(135deg, ${B.navyDark}, ${B.navyMid})` : B.navy,
              }}
            >
              {card.id === "reports"
                ? "View My Reports"
                : `Open ${card.badge.charAt(0) + card.badge.slice(1).toLowerCase()} Assistant`}
            </Button>
          </Box>

          {/* Note */}
          {card.note && (
            <Typography sx={{
              mt: 2, fontSize: 12, color: isDark ? "#64748b" : "#94a3b8",
              lineHeight: 1.6, fontStyle: "italic",
            }}>
              {card.note}
            </Typography>
          )}

          {/* Disclaimer */}
          {card.disclaimer && (
            <Typography sx={{
              mt: 1, fontSize: 12, color: isDark ? "#64748b" : "#8694aa",
              lineHeight: 1.55,
            }}>
              {card.disclaimer}
            </Typography>
          )}
        </Box>
      </Box>
    </motion.div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user } = useContext(UserContext);

  const firstName       = user?.name?.split(" ")[0] || "there";
  const bg              = isDark ? "#080d1a" : "#f4f7ff";
  const isClinician     = user?.role === "clinician";
  const paymentVerified = !isClinician && user?.payment_confirmed === true;
  const reportsEnabled = process.env.REACT_APP_LAB_REPORTS_ENABLED === "true";
  // 2026-10-18 fix — same convention as reportsEnabled above (and
  // LabReports.js's own REPORTS_ENABLED): a kill switch for isolated
  // testing of this one feature, must match the backend's
  // MONTHLY_DIGEST_ENABLED (not auto-synced — set both when toggling).
  const digestEnabled = process.env.REACT_APP_MONTHLY_DIGEST_ENABLED === "true";
  // 2026-10-22 addition — SUMMARIES_CARD below used to be always shown,
  // unconditionally ("not gated behind the GCS-browsing env var" per its
  // own 2026-10-07 comment) — now gated behind its own flag so the card
  // (and the /my-summaries page it links to) can be hidden from real
  // patients until the AI summaries' accuracy is confirmed, same kill-
  // switch convention as every other flag on this page.
  const mySummariesEnabled = process.env.REACT_APP_MY_SUMMARIES_ENABLED === "true";
  // "My Results" ecosystem plan — same kill-switch convention as
  // digestEnabled above. One shared flag for both the patient "My
  // Results" card below AND the clinician "My Patients' Results" card
  // (merged 2026-10-22 per explicit user instruction — previously two
  // independent flags, REACT_APP_MY_RESULTS_ENABLED/REACT_APP_MY_RESULTS_
  // CLINICIAN_ENABLED).
  const myResultsEnabled = process.env.REACT_APP_MY_RESULTS_ENABLED === "true";
  const CARDS = [
    isClinician ? CLINICIAN_CARD : PATIENT_CARD,
    ...(isClinician ? [ORDER_TEST_CARD, MY_PATIENTS_CARD] : []), // Order a Test — clinicians only
    ...(isClinician && myResultsEnabled ? [MY_PATIENTS_RESULTS_CARD] : []),
    ...(isClinician && mySummariesEnabled ? [MY_PATIENTS_SUMMARIES_CARD] : []),
    ...(reportsEnabled ? [REPORTS_CARD] : []),
    // 2026-10-22 fix (user caught this live, logged in as a clinician) —
    // this card was shown to EVERY role, including clinicians, even
    // though /my-summaries 404s any non-patient role server-side
    // (user.get("role", "patient") != "patient") — a dead link on the
    // clinician dashboard, not a real second feature. Now patient-only,
    // same role-gating convention as MY_RESULTS_CARD right below it.
    ...(!isClinician && mySummariesEnabled ? [SUMMARIES_CARD] : []),
    ...(isClinician || !myResultsEnabled ? [] : [MY_RESULTS_CARD]),
    ...(isClinician ? [] : [PAYMENTS_CARD]), // patients only — clinicians have no payment_status field
    ...(isClinician || !digestEnabled ? [] : [MY_DIGEST_CARD]),
  ];

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg,
      display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* Dot-grid */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        backgroundImage: isDark ? DOT_GRID_DARK : DOT_GRID_LIGHT,
        backgroundSize: "28px 28px", opacity: 0.9,
      }} />

      {/* Ambient blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-8%",  left: "-6%",  size: 560, r: 0,   color: isDark ? "rgba(0,53,122,0.22)" : "rgba(0,53,122,0.07)" },
          { top: "55%",  right: "-6%", size: 420, r: 2.5, color: isDark ? "rgba(192,168,140,0.09)" : "rgba(192,168,140,0.13)" },
          { top: "20%",  left: "55%",  size: 340, r: 1.2, color: isDark ? "rgba(0,31,77,0.2)"   : "rgba(59,130,246,0.05)" },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size,
            borderRadius: "50%", background: b.color, filter: "blur(100px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 10 + i * 3, repeat: 2, delay: b.r, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        style={{ position: "relative", zIndex: 20, flexShrink: 0 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.85)" : "rgba(0,31,77,0.97)",
          backdropFilter: "blur(16px)", borderBottom: `1px solid rgba(201,169,110,0.2)`,
        }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
              <NavUserMenu />
            </Stack>
          </Box>
        </Box>
      </motion.div>

      {/* Hero */}
      <Box sx={{ position: "relative", zIndex: 10, textAlign: "center",
        pt: { xs: 7, md: 10 }, pb: { xs: 5, md: 7 }, px: 3 }}>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}>
          <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, letterSpacing: "0.06em",
            color: isDark ? "#64748b" : "#94a3b8", mb: 1.5, textTransform: "uppercase" }}>
            {getGreeting()}, {firstName}
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.18 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "center", mb: 3.5 }}>
            {/* Live status pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              bgcolor: isDark ? "rgba(192,168,140,0.1)" : "rgba(0,53,122,0.07)",
              border: `1px solid ${isDark ? "rgba(192,168,140,0.2)" : "rgba(0,53,122,0.12)"}`,
              borderRadius: "99px", px: 2.5, py: 0.75,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: B.gold,
                "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.25 } },
                animation: "pulse 2s infinite" }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: B.gold, letterSpacing: "0.14em" }}>
                LABARIUM AI — ACTIVE
              </Typography>
            </Box>

            {/* Payment verified badge — patients only */}
            {paymentVerified && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35, type: "spring", stiffness: 260, damping: 18 }}>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.75,
                  bgcolor: isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.3)",
                  borderRadius: "99px", px: 2, py: 0.75,
                }}>
                  <Box sx={{ fontSize: 13.5, lineHeight: 1 }}>✓</Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#16a34a", letterSpacing: "0.1em" }}>
                    LABARIUM LAB TEST COPAY AND ACTIVATION FEE VERIFIED
                  </Typography>
                </Box>
              </motion.div>
            )}
          </Box>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}>
          <Typography sx={{
            fontSize: { xs: 30, sm: 40, md: 48 }, fontWeight: 800,
            color: isDark ? "#f1f5f9" : B.navy,
            lineHeight: 1.12, mb: 2.5, maxWidth: 680, mx: "auto",
            letterSpacing: "-0.02em",
          }}>
            How would you like to use the Labarium{" "}
            <Box component="span" sx={{
              background: `linear-gradient(135deg, ${B.gold} 10%, ${B.goldLight} 60%, ${B.gold} 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              AI Assistant?
            </Box>
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}>
          <Typography sx={{
            fontSize: { xs: 15, sm: 16 }, color: isDark ? "#94a3b8" : "#3d4f6b",
            maxWidth: 460, mx: "auto", lineHeight: 1.85, mb: 4.5,
          }}>
            Select your role below. Your session is end-to-end encrypted and
            fully compliant with HIPAA data standards.
          </Typography>
        </motion.div>

        {/* 2026-10-08 fix — reported directly: these two rows center
            against the full page width while the card grid below them
            centers against a 1240px container, so on a wide screen they
            drift off the cards' own center axis. Pinning both to the
            same maxWidth/mx:auto as the cards makes the whole page read
            as one consistent column instead of two independently-centered
            blocks that happen to mostly line up. */}
        <Box sx={{ maxWidth: 1240, mx: "auto", width: "100%" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.36 }}>
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 5, gap: "8px !important" }}>
              {TRUST.map((t) => (
                <Chip key={t}
                  icon={<VerifiedIcon sx={{ fontSize: "13px !important", color: `${B.gold} !important` }} />}
                  label={t} size="small"
                  sx={{
                    fontSize: 13, fontWeight: 600, height: 28,
                    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
                    color: isDark ? "#94a3b8" : "#475569",
                    "& .MuiChip-icon": { ml: 1 },
                  }}
                />
              ))}
            </Stack>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.42 }}>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ gap: "12px !important" }}>
              {STATS.map((s) => (
                <StatPill key={s.label} value={s.value} label={s.label} isDark={isDark} />
              ))}
            </Stack>
          </motion.div>
        </Box>
      </Box>

      {/* Cards — 2026-10-08 fix, reported directly ("layout/alignment
          looks off... is that visually aesthetic and comfortable"): this
          used to be a single flex row forcing every card onto one line.
          Fine for 2-3 cards (patient view), but a clinician gets 5
          (Clinician/Order Test/My Patients/Summaries, +Reports if
          enabled) squeezed into the same 980px container — each card
          dropped to ~160px wide, cramming the description/feature text
          into awkward wraps. A real CSS grid that wraps at a sane
          minimum card width fixes this for any card count, not just a
          one-off tweak for 5. Widened the container too, since a wrapped
          grid actually benefits from more breathing room than the old
          fixed single-row layout did. */}
      <Box sx={{
        flex: 1, position: "relative", zIndex: 10,
        maxWidth: 1240, mx: "auto", width: "100%",
        px: { xs: 2, sm: 3 }, pb: 8,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: { xs: 3, md: 4 },
        alignItems: "stretch",
      }}>
        {CARDS.map((card, i) => (
          <motion.div key={card.id}
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.38 + i * 0.14, ease: [0.34, 1.56, 0.64, 1] }}
            style={{ height: "100%" }}
          >
            <RoleCard card={card} isDark={isDark} onClick={() => navigate(card.path)} />
          </motion.div>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.9)" : B.navyDark,
        backdropFilter: "blur(10px)", py: 3, px: 3,
        position: "relative", zIndex: 10,
        borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0,
      }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex",
          alignItems: "center", justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.4)", letterSpacing: "0.04em" }}>
            © {new Date().getFullYear()} Labarium Diagnostic Systems, Inc. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.35)" }}>
              AI analysis does not constitute medical advice.
            </Typography>
            <Box component={Link} to="/terms"
              sx={{ fontSize: 13.5, color: "rgba(192,168,140,0.7)", textDecoration: "none",
                "&:hover": { color: B.gold } }}>
              Terms of Use
            </Box>
            <Box component={Link} to="/privacy"
              sx={{ fontSize: 13.5, color: "rgba(192,168,140,0.7)", textDecoration: "none",
                "&:hover": { color: B.gold } }}>
              Privacy Policy
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
